import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import type {
  ChangeProviderStatusResult,
  ProviderStatus,
  ProviderStatusChangeReason,
} from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import {
  DEFAULT_PROVIDER_REASON,
  noteError,
  noteIsValid,
  NOTE_MAX,
  PROVIDER_CHANGE_CONSEQUENCE,
  PROVIDER_CHANGE_TITLE,
  PROVIDER_REASONS,
  refusalTitle,
} from './provider-status.ts';

/** What was asked for, before the reason is given. */
export interface PendingProviderChange {
  id: string;
  name: string;
  from: ProviderStatus;
  to: ProviderStatus;
}

/**
 * The confirm-and-say-why step in front of a provider status change.
 *
 * The route refuses without a reason code from the closed set AND a note of at
 * least ten characters, so the form will not let an empty one be sent: a 400
 * discovered after the click teaches the operator that the screen does not know
 * its own rules.
 *
 * A failure opens the house error modal rather than a toast. A toast can be
 * missed, and an operator who misses "the provider did NOT go into maintenance"
 * carries on believing the shelf is closed.
 */
export function ProviderStatusDialog({
  pending,
  holdsKey,
  mfa,
  onClose,
  onChanged,
}: {
  pending: PendingProviderChange | null;
  /**
   * HOLDS THE PERMISSION — not "may write". It used to be handed `canManage`,
   * which is the permission AND a two-factor-verified session folded into one
   * boolean, and it feeds the `allowed` branch below. So a key-holder on an
   * unverified session arrived here with `allowed: false` and was told "that
   * needs the games.manage permission", which they have. The next branch down
   * would have named the real wall.
   *
   * The two walls are separate questions and the copy already distinguishes
   * them, so the props have to as well.
   */
  holdsKey: boolean;
  /** Whether THIS session was verified with a second factor. */
  mfa: boolean;
  onClose: () => void;
  onChanged: (result: ChangeProviderStatusResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [reasonCode, setReasonCode] = useState<ProviderStatusChangeReason>('SCHEDULED_MAINTENANCE');
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Each opening starts clean: a reason typed for one provider must never be
     waiting in the box for the next one. The default follows the direction
     asked for, which is a suggestion and not an answer. */
  useEffect(() => {
    if (!pending) return;
    setReasonCode(DEFAULT_PROVIDER_REASON[pending.to]);
    setNote('');
    setNoteTouched(false);
  }, [pending]);

  const submit = useCallback(async () => {
    if (!pending || !noteIsValid(note)) return;
    setSubmitting(true);
    try {
      const result = await api.post<ChangeProviderStatusResult>(
        `/api/games/providers/${pending.id}/status`,
        { status: pending.to, reasonCode, note: note.trim() },
      );
      /* The count is the point, so it is stated on the screen as well as here —
         see the notice the Providers tab keeps after a change. */
      toast(`${result.name} is now ${result.status.toLowerCase()}. The reason is in the provider log.`);
      onChanged(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      /* Branch only on codes the platform defines. Anything else — a newer
         server, a proxy inventing its own — falls through to the generic
         heading with the server's own sentence, which is never wrong. */
      const code = isErrorCode(raw) ? raw : undefined;
      showError({
        title: refusalTitle(code, pending.name, { allowed: holdsKey, mfa }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [pending, note, reasonCode, holdsKey, mfa, onChanged, onClose, showError, toast]);

  const error = noteError(note, noteTouched);

  return (
    <Dialog
      open={pending !== null}
      onClose={onClose}
      title={pending ? PROVIDER_CHANGE_TITLE[pending.to](pending.name) : ''}
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!noteIsValid(note) || submitting}
            onClick={() => void submit()}
          >
            {pending ? PROVIDER_CHANGE_TITLE[pending.to](pending.name).replace('?', '') : 'Confirm'}
          </Button>
        </>
      }
    >
      {pending && (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>{PROVIDER_CHANGE_CONSEQUENCE[pending.to]}</div>
          </div>

          <Field
            label="Reason"
            hint="A closed set, because the provider log is a report as well as a story."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value as ProviderStatusChangeReason)}
              >
                {PROVIDER_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            error={error}
            hint="At least 10 characters. The code says what kind of thing happened; the note says what actually did."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => setNoteTouched(true)}
                placeholder="SA confirmed a settlement fault on their rail at 02:10; ETA two hours."
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
